import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import styled from 'styled-components/native';
import { WhereClause } from '@people-science/query-core';
import ActivityIndicatorScreen from '../../../../components/ActivityIndicatorScreen';
import AlertBox from '../../../../components/AlertBox';
import GaugeChart from '../../../../components/charts/GaugeChart';
import Spacer from '../../../../components/Spacer';
import { B, P } from '../../../../components/Typography';
import {
  ImpactGaugeMeasurementAlgorithm,
  useQueryStudyParticipantImpact
} from '../../../../hooks/study-participant/useQueryStudyParticipantImpact';
import {
  ScreenComponentPlugin,
  ScreenComponentProps
} from '../ScreenComponentPlugin';
import { FlatList, Platform, View } from 'react-native';
import { Octicons } from '@expo/vector-icons';
import IconButton from '../../../../components/IconButton';
import WebViewModal from '../../../modals/WebViewModal';
import Card from '../../../../components/Card';
import elementIDs from '../../../../../elementIDs';

const StyledCard = styled(Card)`
  width: 100%;
  padding: 13px 0 13px 0;
`;

const StyledText = styled.Text`
  font-size: 24px;
  font-family: 'WorkSans-Medium';
`;

const StyledValueText = styled(P)`
  font-size: 30px;
  line-height: 30px;
`;

const StyledMetricView = styled.View`
  flex-direction: row;
  background: #fff5e7;
  padding: 14px;
  margin-bottom: 12px;
  justify-content: space-between;
  align-items: center;
`;

const StyledItemContainer = styled.View`
  ${Platform.OS === 'web' ? 'margin-bottom: 6px' : ''}
`;

const StyledSeparator = styled.View`
  width: 12px;
`;

const StyledIconButton = styled(IconButton)`
  align-self: flex-end;
  margin-right: 18px;
`;

const StyledTouchable = styled.TouchableOpacity<{
  color: string;
}>`
  background-color: ${p => p.color};
  height: 40px;
  border-radius: 50px;
  padding: 10px 18px 10px 18px;
  align-items: center;
  justify-content: center;
`;

const StyledItemText = styled(B)`
  font-size: 14px;
  line-height: 18px;
  letter-spacing: 0.75px;
`;

const StyledImpactResultContainer = styled.View`
  background-color: white;
  border-radius: 8px;
  flex-direction: row;
  height: 62px;
  padding-horizontal: 5px;
  align-items: center;
`;

const StyledMetricUnitText = styled(P)`
  margin-top: -2px;
`;

export interface ImpactGaugeMeasurement {
  buttonLabel?: string;
  metricTitle?: string;
  metricUnit?: string;
  metricDescription?: string;
  gaugeTitle?: string;
  gaugeLabel?: string;
  entityId?: string;
  columnName?: string;
  wheres?: WhereClause[];
  /**
   * Use algorithm instead
   * @deprecated
   */
  enableBaselineAverage?: boolean;
  baselineAverage?: number;
  algorithm?: ImpactGaugeMeasurementAlgorithm;
  initialBaselineSampleDayCount?: number;
  comparedLastSampleDayCount?: number;
}

function getSignedNumber(value: string) {
  const number = Number(value);
  if (number > 0) {
    return '+' + number;
  } else {
    return number.toString();
  }
}

export interface ImpactGaugeCardProps {
  title?: string;
  hasMoreInfo?: boolean;
  infoTitle?: string;
  infoHtml?: string;
  measurements?: ImpactGaugeMeasurement[];
  defaultMeasurementIndex?: number;
}

const ImpactGaugeCard: React.FC<ScreenComponentProps<ImpactGaugeCardProps>> = ({
  hasMoreInfo,
  infoTitle,
  infoHtml,
  measurements = [],
  studyParticipant,
  defaultMeasurementIndex
}) => {
  const [selectedMeasurement, setSelectedMeasurement] = useState(
    measurements?.length
      ? measurements[defaultMeasurementIndex || 0]
      : undefined
  );
  // updated selectedMeasurement if measurements array changed; this can
  // happen if user taps another tab and navigates back to the studies tab
  useEffect(() => {
    setSelectedMeasurement(selectedMeasurement => {
      if (
        selectedMeasurement &&
        !measurements.some(m => selectedMeasurement === m)
      ) {
        return measurements[defaultMeasurementIndex || 0];
      } else {
        return selectedMeasurement;
      }
    });
  }, [measurements, defaultMeasurementIndex]);

  const { impactResults, loadingImpact, loadingImpactError } =
    useQueryStudyParticipantImpact(studyParticipant?.id || 0, measurements);
  const impactResult = useMemo(() => {
    if (!selectedMeasurement || !impactResults) return;
    const index = measurements.indexOf(selectedMeasurement);
    if (impactResults[index]) {
      if (
        impactResults[index].averageOffMedication &&
        impactResults[index].averageOnMedication
      ) {
        return impactResults[index];
      }
    }
    return;
  }, [selectedMeasurement, impactResults, measurements]);
  const flatListRef = useRef<FlatList>();
  const scrollToIndex = useCallback((index: number) => {
    setTimeout(() => {
      flatListRef?.current?.scrollToIndex({
        viewPosition: 0.52,
        index: index
      });
    }, 100);
  }, []);
  const [showModal, setShowModal] = useState<boolean>();
  return (
    <StyledCard testID={elementIDs.compareChartID}>
      {/* <StyledTextB color='dark' padding={3}>
        {title || 'Results: Impact'}
      </StyledTextB> */}
      <FlatList
        ref={flatListRef}
        style={{
          alignSelf: 'center'
        }}
        keyExtractor={(_, index) => index.toString()}
        horizontal
        overScrollMode='never'
        showsHorizontalScrollIndicator={Platform.OS === 'web'}
        data={measurements}
        initialNumToRender={10}
        maxToRenderPerBatch={10}
        ItemSeparatorComponent={() => <StyledSeparator />}
        renderItem={listItem => {
          const item = listItem.item;
          return (
            <StyledItemContainer>
              <StyledTouchable
                color={selectedMeasurement === item ? '#FFCAAA' : 'transparent'}
                onPress={() => {
                  scrollToIndex(listItem.index);
                  setSelectedMeasurement(item);
                }}
              >
                <StyledItemText
                  testID={`plugin-impactGauge-button-${item.buttonLabel || ''}`}
                  color='dark'
                >
                  {item.buttonLabel || ''}
                </StyledItemText>
              </StyledTouchable>
            </StyledItemContainer>
          );
        }}
        onLayout={() => {
          if (typeof defaultMeasurementIndex !== 'undefined') {
            flatListRef?.current?.scrollToIndex({
              viewPosition: 1,
              index: defaultMeasurementIndex
            });
          }
        }}
        onScrollToIndexFailed={error => {
          scrollToIndex(error.index);
        }}
      />
      <Spacer size={0.5} />
      <StyledMetricView>
        <View>
          {!!selectedMeasurement?.metricTitle && (
            <P testID={elementIDs.pluginImpactGaugeMetricTitleID} color='dark'>
              {selectedMeasurement.metricTitle}
            </P>
          )}
          {!!selectedMeasurement?.metricDescription && (
            <StyledText
              testID={elementIDs.pluginImpactGaugeMetricDescriptionID}
            >
              {selectedMeasurement.metricDescription}
            </StyledText>
          )}
        </View>
        <StyledImpactResultContainer>
          <StyledValueText color='dark'>
            {impactResult && !isNaN(impactResult.averageChange)
              ? getSignedNumber(
                  Number(impactResult.averageChange.toFixed(2)).toLocaleString()
                )
              : 0}
          </StyledValueText>
          {
            <StyledMetricUnitText
              testID={elementIDs.pluginImpactGaugeMetricUnitID}
              color='dark'
            >
              {selectedMeasurement?.metricUnit}
            </StyledMetricUnitText>
          }
        </StyledImpactResultContainer>
      </StyledMetricView>
      {hasMoreInfo && (
        <StyledIconButton
          onPress={() => setShowModal(true)}
          testID={elementIDs.pluginImpactGaugeInfoID}
        >
          <Octicons name='question' size={24} color='#494753' />
        </StyledIconButton>
      )}
      <Spacer size={hasMoreInfo ? 2 : 4.5} />
      <GaugeChart
        value={getImpactMeterProgressValue(impactResult?.p)}
        values={[
          'Uncertain',
          'Possible',
          'Likely',
          'Highly Likely',
          'Almost Certain'
        ]}
        label={selectedMeasurement?.gaugeLabel || ''}
      />
      {loadingImpact && <ActivityIndicatorScreen modal />}
      {loadingImpactError && (
        <AlertBox fullWidth severity='error'>
          Chart error: {loadingImpactError.message}
        </AlertBox>
      )}
      {showModal && (
        <WebViewModal
          contentPadding
          visible={showModal}
          title={infoTitle || ''}
          html={infoHtml || ''}
          onClose={() => setShowModal(false)}
        />
      )}
    </StyledCard>
  );
};

export function getImpactMeterProgressValue(
  pValue: number | undefined
): number {
  if (pValue === undefined) {
    return 0;
  }
  const value = 1 - pValue;
  let progress;
  if (value >= 0.99) {
    progress = 1;
  } else if (value >= 0.95 && value <= 0.9899999) {
    progress = 0.75;
  } else if (value >= 0.9 && value <= 0.9499999) {
    progress = 0.5;
  } else if (value >= 0.8 && value <= 0.89999) {
    progress = 0.25;
  } else {
    progress = 0.02;
  }
  return progress;
}

export const ImpactGaugeCardPlugin: ScreenComponentPlugin<ImpactGaugeCardProps> =
  {
    type: 'ImpactGaugeCard',
    name: 'Impact Gauge Card',
    categories: ['study-progress', 'my-chloe'],
    component: ImpactGaugeCard
  };
